import { useRuntimeConfig } from "#app";
import { useIDBKeyval } from "#staxmpc/composables/useIDBKeyval";
import { Uint8ArrayCrypto } from "#staxmpc/lib/aes";
import { dklsLoader, SignAlgo } from "#staxmpc/lib/dkls";
import { decodeBase64, encodeBase64, encodeBytesWithPrefixedLength, isEqualUint8Array, parseBytesWithPrefixedLength } from "#staxmpc/lib/encode";
import { getPublicKey, Keyshare, SchnorrKeyshare, verifyingKey } from "@stavax/schnorr-wasm";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
export const useMPCStore = (storeKey = "default") => {
  return defineStore(`mpcStore-${storeKey}`, () => {
    const stavaxMPCConfig = useRuntimeConfig().public.stavaxMPC;
    const dependenciesLoaded = ref(false);
    const isUnlocked = ref(false);
    const currentPassword = ref();
    const storeData = ref(new MPCStoreData(void 0, void 0));
    const {
      data: encryptedData,
      isFinished: isEncryptedDataLoaded,
      set: setEncryptedData
    } = useIDBKeyval(`${storeKey}_data`, void 0, {
      customDbName: `${stavaxMPCConfig.idbName || "stavax-mpc"}_${storeKey}`,
      customStoreName: stavaxMPCConfig.idbStoreName || "main"
    });
    const dependenciesLoader = Promise.all([
      dklsLoader,
      new Promise((resolve) => {
        const stop = watch(isEncryptedDataLoaded, () => {
          if (isEncryptedDataLoaded.value) {
            stop();
            resolve(void 0);
          }
        });
      })
    ]).then(() => dependenciesLoaded.value = true);
    async function writeEncryptedData() {
      if (!storeData.value.share1) {
        return setEncryptedData(void 0);
      }
      return setEncryptedData(await Uint8ArrayCrypto.encrypt(storeData.value.toBytes(), currentPassword.value));
    }
    function requireCorrectPassword(pass) {
      if (pass != currentPassword.value) {
        throw new Error("Incorrect Password");
      }
    }
    async function requireUnlocked(action) {
      return requireUnlockStatus(true, action);
    }
    async function requireLocked(action) {
      return requireUnlockStatus(false, action);
    }
    async function requireUnlockStatus(unlocked, action) {
      if (!dependenciesLoaded.value) {
        await dependenciesLoader;
      }
      if (isUnlocked.value !== unlocked) {
        if (isUnlocked.value) {
          throw new Error("mpcStore is unlocked, can't " + action);
        }
        throw new Error("mpcStore is locked, can't " + action);
      }
    }
    async function exportShare(pass, share) {
      await requireUnlocked("export share");
      requireCorrectPassword(pass);
      if (!share) {
        throw new Error("share not found");
      }
      return encodeBase64(await Uint8ArrayCrypto.encrypt(share.toBytes(), currentPassword.value));
    }
    return {
      isReady: computed(() => dependenciesLoaded.value),
      isUnlocked: computed(() => isUnlocked.value),
      isLocked: computed(() => !isUnlocked.value),
      isEmpty: computed(() => !encryptedData.value),
      shares: computed(() => {
        if (!dependenciesLoaded.value || !isUnlocked.value || !storeData.value) {
          return new MPCStoreData();
        }
        return storeData.value;
      }),
      async setSharesByAlgo(algo, share1, share2) {
        await requireUnlocked("set shares");
        if (!share1) {
          throw new Error("share1 is required");
        }
        let share1MultiAlgo = void 0;
        if (algo == SignAlgo.ECDSA) {
          share1MultiAlgo = new MPCMultiAlgoShare(share1, storeData.value.share1?.eddsa);
        } else if (algo == SignAlgo.EdDSA) {
          share1MultiAlgo = new MPCMultiAlgoShare(storeData.value.share1?.ecdsa, share1);
        } else {
          throw new Error("invalid algo");
        }
        let share2MultiAlgo = void 0;
        if (share2) {
          if (algo == SignAlgo.ECDSA) {
            share2MultiAlgo = new MPCMultiAlgoShare(share2, storeData.value.share2?.eddsa);
          } else if (algo == SignAlgo.EdDSA) {
            share2MultiAlgo = new MPCMultiAlgoShare(storeData.value.share2?.ecdsa, share2);
          } else {
            throw new Error("invalid algo");
          }
        }
        storeData.value = new MPCStoreData(share1MultiAlgo, share2MultiAlgo);
        return writeEncryptedData();
      },
      lock() {
        isUnlocked.value = false;
        currentPassword.value = void 0;
      },
      async unlock(pass) {
        if (!dependenciesLoaded.value) {
          await dependenciesLoader;
        }
        if (!pass) {
          throw new Error("Password is required");
        }
        if (isUnlocked.value && pass != currentPassword.value) {
          throw new Error("Incorrect Password");
        }
        if (encryptedData.value) {
          const data = await Uint8ArrayCrypto.decrypt(encryptedData.value, pass);
          if (!data) {
            throw new Error("Invalid Password");
          }
          storeData.value = MPCStoreData.fromBytes(data);
        }
        currentPassword.value = pass;
        isUnlocked.value = true;
      },
      async setPassword(pass) {
        await requireUnlocked("set password");
        currentPassword.value = pass;
      },
      async checkPassword(pass) {
        await requireUnlocked("check password");
        return pass === currentPassword.value;
      },
      async exportShare1(pass) {
        return exportShare(pass, storeData.value.share1);
      },
      async exportShare2(pass) {
        return exportShare(pass, storeData.value.share2);
      },
      async importShare1(encryptedBase64, pass) {
        await requireLocked("import share1");
        if (storeData.value.share1) {
          throw new Error("share1 already exists");
        }
        const encryptedBytes = decodeBase64(encryptedBase64);
        const share1Bytes = await Uint8ArrayCrypto.decrypt(encryptedBytes, pass);
        storeData.value = new MPCStoreData(MPCMultiAlgoShare.fromBytes(share1Bytes), void 0);
        currentPassword.value = pass;
        await writeEncryptedData();
        isUnlocked.value = true;
      },
      async importShare2(encryptedBase64, pass) {
        await requireUnlocked("import share2");
        if (!storeData.value.share1) {
          throw new Error("share1 is required when import share2");
        }
        if (storeData.value.share2) {
          throw new Error("share2 already exists");
        }
        const encryptedBytes = decodeBase64(encryptedBase64);
        const share2Bytes = await Uint8ArrayCrypto.decrypt(encryptedBytes, pass);
        const share1 = storeData.value.share1;
        const share2 = MPCMultiAlgoShare.fromBytes(share2Bytes);
        share1.checkCompatibility(share2);
        storeData.value = new MPCStoreData(share1, share2);
        return writeEncryptedData();
      },
      async clear(pass) {
        await requireUnlocked("clear");
        if (pass != currentPassword.value) {
          throw new Error("Incorrect Password");
        }
        storeData.value = new MPCStoreData();
        await writeEncryptedData();
        isUnlocked.value = false;
        currentPassword.value = void 0;
      }
    };
  })();
};
const VersionPrefixLength = 32;
const algoIDByAlgo = {
  [SignAlgo.ECDSA]: 1,
  [SignAlgo.EdDSA]: 2
};
const algoByAlgoID = Object.fromEntries(
  Object.entries(algoIDByAlgo).map(([key, value]) => [value, key])
);
export class MPCShare {
  static _version = 1;
  _verifyingKey;
  _secretKey;
  _encryptionKey;
  _decryptionKey;
  _algo;
  _share;
  constructor(algo, secretKey, share, decryptionKey) {
    this._algo = algo;
    this._verifyingKey = verifyingKey(secretKey);
    this._secretKey = secretKey;
    if (decryptionKey && decryptionKey.length > 0) {
      this._encryptionKey = getPublicKey(decryptionKey);
      this._decryptionKey = decryptionKey;
    }
    this._share = share;
  }
  checkCompatibility(share) {
    if (!isEqualUint8Array(this._share.keyId(), share._share.keyId())) {
      throw new Error("keyId of two share miss match");
    }
    if (!isEqualUint8Array(this._share.publicKey(), share._share.publicKey())) {
      throw new Error("publicKey of two share miss match");
    }
    if (this._share.partyId() == share._share.partyId()) {
      throw new Error("two share have same partyId");
    }
  }
  get verifyingKey() {
    return this._verifyingKey;
  }
  get secretKey() {
    return this._secretKey;
  }
  get encryptionKey() {
    return this._encryptionKey;
  }
  get decryptionKey() {
    return this._decryptionKey;
  }
  get share() {
    return this._share;
  }
  /*
    Data v0:
      Layout: <32 bytes secret data><share data>
    Data v1 (current):
      Description: We need a way to determine the memory layout of v0,
        show I put 32 empty bytes at the start of data since no data from v1 can contain data like this.
      Layout: <32 bytes empty><1 byte version><1 byte algo>
              <4 bytes _secretKey length><_secretKey data>
              <4 bytes _decryptionKey length>[_decryptionKey data]
              <4 bytes share length><share data>
   */
  toBytes() {
    return new Uint8Array([
      ...new Uint8Array(VersionPrefixLength),
      MPCShare._version,
      algoIDByAlgo[this._algo],
      ...encodeBytesWithPrefixedLength(
        this._secretKey,
        this._decryptionKey || new Uint8Array(),
        this._share.toBytes()
      )
    ]);
  }
  static fromBytes(bytes) {
    if (bytes.length <= 32) {
      throw new Error("Invalid bytes length");
    }
    if (!MPCShare.hasVersionPrefix(bytes)) {
      const secretKey2 = bytes.slice(0, 32);
      const share = Keyshare.fromBytes(bytes.slice(32));
      return new MPCShare(SignAlgo.ECDSA, secretKey2, share);
    }
    bytes = bytes.slice(33);
    const algoID = bytes[0];
    bytes = bytes.slice(1);
    const [secretKey, decryptionKey, shareBytes] = parseBytesWithPrefixedLength(bytes);
    const algo = algoByAlgoID[algoID];
    const keyshare = algo == SignAlgo.EdDSA ? SchnorrKeyshare.fromBytes(shareBytes) : Keyshare.fromBytes(shareBytes);
    return new MPCShare(algo, secretKey, keyshare, decryptionKey);
  }
  static hasVersionPrefix(bytes) {
    if (bytes.length < VersionPrefixLength) {
      return false;
    }
    return bytes.slice(0, VersionPrefixLength).every((v) => v === 0);
  }
}
class MPCMultiAlgoShare {
  _ecdsa;
  _eddsa;
  constructor(ecdsaShare, eddsaShare) {
    this._ecdsa = ecdsaShare;
    this._eddsa = eddsaShare;
  }
  get ecdsa() {
    return this._ecdsa;
  }
  get eddsa() {
    return this._eddsa;
  }
  checkCompatibility(share) {
    if (!!this._ecdsa != !!share._ecdsa) {
      throw new Error("two share not contain same ECDSA part");
    }
    this._ecdsa && this._ecdsa.checkCompatibility(share._ecdsa);
    if (!!this._eddsa != !!share._eddsa) {
      throw new Error("two share not contain same EdDSA part");
    }
    this._eddsa && this._eddsa.checkCompatibility(share._eddsa);
  }
  toBytes() {
    return encodeBytesWithPrefixedLength(
      this._ecdsa?.toBytes() || new Uint8Array(),
      this._eddsa?.toBytes() || new Uint8Array()
    );
  }
  static fromBytes(bytes) {
    let ecdsa = void 0;
    let eddsa = void 0;
    const data = parseBytesWithPrefixedLength(bytes);
    if (data.length < 2) {
      throw new Error("Invalid MPCMultiAlgoShare data");
    }
    const [ecdsaBytes, eddsaBytes] = data;
    if (ecdsaBytes.length > 0) {
      ecdsa = MPCShare.fromBytes(ecdsaBytes);
    }
    if (eddsaBytes.length > 0) {
      eddsa = MPCShare.fromBytes(eddsaBytes);
    }
    return new MPCMultiAlgoShare(ecdsa, eddsa);
  }
}
class MPCStoreData {
  _share1;
  _share2;
  constructor(share1, share2) {
    this._share1 = share1;
    this._share2 = share2;
  }
  get share1() {
    return this._share1;
  }
  get share2() {
    return this._share2;
  }
  toBytes() {
    return encodeBytesWithPrefixedLength(
      this._share1?.toBytes() || new Uint8Array(),
      this._share2?.toBytes() || new Uint8Array()
    );
  }
  static fromBytes(bytes) {
    let share1 = void 0;
    let share2 = void 0;
    const data = parseBytesWithPrefixedLength(bytes);
    if (data.length < 2) {
      throw new Error("Invalid MPCMultiAlgoShare data");
    }
    const [share1Bytes, share2Bytes] = data;
    if (share1Bytes.length > 0) {
      share1 = MPCMultiAlgoShare.fromBytes(share1Bytes);
    }
    if (share2Bytes.length > 0) {
      share2 = MPCMultiAlgoShare.fromBytes(share2Bytes);
    }
    return new MPCStoreData(share1, share2);
  }
}
